/*
  Wine By the Case predictive search.
  Drives any [data-predictive-search] container:
    required children: input[name="q"], [data-predictive-panel], [role="listbox"]
    optional child:    [data-predictive-status] (visually hidden live region)
    container data:    data-search-url (routes.search_url), data-money-format (shop.money_format)
  Uses the /search/suggest.json JSON API, debounced 200ms, rendered client side
  with bottle thumbs in mini .bottle-frame elements. Full keyboard support:
  arrows move the active option, Enter follows it, Escape closes.
*/
(function () {
  'use strict';

  var DEBOUNCE_MS = 200;
  var MIN_CHARS = 2;
  var LIMIT = 8;

  var BOTTLE_SVG =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M10 2h4v4l2 3v11a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2V9l2-3V2Z"/></svg>';

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoney(value, format) {
    var num = parseFloat(value);
    if (isNaN(num)) return '';
    var amount = /no_decimals/.test(format || '') ? String(Math.round(num)) : num.toFixed(2);
    if (format && format.indexOf('{{') !== -1) {
      return format.replace(/\{\{\s*[^}]+\s*\}\}/, amount);
    }
    return '$' + amount;
  }

  function sizedImage(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function PredictiveSearch(container) {
    this.container = container;
    this.input = container.querySelector('input[name="q"]');
    this.panel = container.querySelector('[data-predictive-panel]');
    this.listbox = container.querySelector('[role="listbox"]');
    this.status = container.querySelector('[data-predictive-status]');
    if (!this.input || !this.panel || !this.listbox) return;

    this.searchUrl = container.getAttribute('data-search-url') || '/search';
    this.moneyFormat = container.getAttribute('data-money-format') || '';
    this.idBase = this.listbox.id || 'PredictiveList';
    this.timer = null;
    this.controller = null;
    this.activeIndex = -1;
    this.options = [];
    this.lastQuery = '';
    this.isOpen = false;

    this.bind();
  }

  PredictiveSearch.prototype.bind = function () {
    var self = this;

    this.input.addEventListener('input', function () {
      var query = self.input.value.trim();
      window.clearTimeout(self.timer);
      if (query.length < MIN_CHARS) {
        self.lastQuery = '';
        self.close();
        return;
      }
      self.timer = window.setTimeout(function () {
        self.fetchResults(query);
      }, DEBOUNCE_MS);
    });

    this.input.addEventListener('focus', function () {
      if (self.options.length && self.input.value.trim().length >= MIN_CHARS) {
        self.open();
      }
    });

    this.input.addEventListener('keydown', function (event) {
      self.onKeydown(event);
    });

    document.addEventListener('click', function (event) {
      if (!self.container.contains(event.target)) {
        self.close();
      }
    });
  };

  PredictiveSearch.prototype.onKeydown = function (event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!this.options.length) return;
      event.preventDefault();
      if (!this.isOpen) this.open();
      var delta = event.key === 'ArrowDown' ? 1 : -1;
      var next = this.activeIndex + delta;
      if (next < 0) next = this.options.length - 1;
      if (next >= this.options.length) next = 0;
      this.setActive(next);
    } else if (event.key === 'Enter') {
      if (this.isOpen && this.activeIndex > -1 && this.options[this.activeIndex]) {
        event.preventDefault();
        var href = this.options[this.activeIndex].getAttribute('href');
        if (href) window.location.assign(href);
      }
    } else if (event.key === 'Escape') {
      if (this.isOpen) {
        event.preventDefault();
        this.close();
      }
    } else if (event.key === 'Tab') {
      this.close();
    }
  };

  PredictiveSearch.prototype.setActive = function (index) {
    var self = this;
    this.activeIndex = index;
    this.options.forEach(function (option, i) {
      var selected = i === index;
      option.setAttribute('aria-selected', selected ? 'true' : 'false');
      if (selected) {
        self.input.setAttribute('aria-activedescendant', option.id);
        if (typeof option.scrollIntoView === 'function') {
          option.scrollIntoView({ block: 'nearest' });
        }
      }
    });
    if (index < 0) {
      this.input.removeAttribute('aria-activedescendant');
    }
  };

  PredictiveSearch.prototype.fetchResults = function (query) {
    var self = this;
    if (this.controller) this.controller.abort();
    this.controller = typeof AbortController === 'function' ? new AbortController() : null;

    var url = this.searchUrl.replace(/\/+$/, '') + '/suggest.json' +
      '?q=' + encodeURIComponent(query) +
      '&resources[type]=product' +
      '&resources[limit]=' + LIMIT +
      '&resources[options][unavailable_products]=last';

    fetch(url, this.controller ? { signal: this.controller.signal } : undefined)
      .then(function (response) {
        if (!response.ok) throw new Error('Suggest request failed: ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var results = data && data.resources && data.resources.results ? data.resources.results : {};
        var products = results.products || [];
        if (self.input.value.trim() !== query) return;
        self.lastQuery = query;
        self.render(products, query);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        self.close();
      });
  };

  PredictiveSearch.prototype.render = function (products, query) {
    var self = this;
    var html = '';

    /* The $2 personalized gift card is a cart add-on. It stays published so
       /cart/add.js accepts it, so drop it from the suggestions here. */
    products = products.filter(function (product) {
      return product.handle !== 'personalized-gift-card' && product.handle !== 'gift-bag';
    });

    products.forEach(function (product, index) {
      var imageUrl = '';
      if (product.featured_image && product.featured_image.url) {
        imageUrl = product.featured_image.url;
      } else if (product.image) {
        imageUrl = product.image;
      }

      var thumb;
      if (imageUrl) {
        thumb = '<img class="bottle-img" src="' + escapeHtml(sizedImage(imageUrl, 120)) +
          '" alt="" loading="lazy" width="60" height="75">';
      } else {
        thumb = '<span class="predictive__glyph">' + BOTTLE_SVG + '</span>';
      }

      var priceHtml = formatMoney(product.price, self.moneyFormat);
      var compareHtml = '';
      if (parseFloat(product.compare_at_price_max || 0) > parseFloat(product.price || 0)) {
        compareHtml = '<s class="predictive__compare">' +
          formatMoney(product.compare_at_price_max, self.moneyFormat) + '</s>';
      }

      html += '<a class="predictive__item" role="option" aria-selected="false" id="' +
        self.idBase + '-opt-' + index + '" href="' + escapeHtml(product.url) + '">' +
        '<span class="bottle-frame predictive__thumb">' + thumb + '</span>' +
        '<span class="predictive__meta">' +
        (product.vendor ? '<span class="predictive__vendor">' + escapeHtml(product.vendor) + '</span>' : '') +
        '<span class="predictive__title">' + escapeHtml(product.title) + '</span>' +
        '<span class="predictive__price">' + priceHtml + compareHtml + '</span>' +
        '</span>' +
        '</a>';
    });

    html += '<a class="predictive__all" role="option" aria-selected="false" id="' +
      this.idBase + '-opt-all" href="' + escapeHtml(this.searchUrl) + '?q=' +
      encodeURIComponent(query) + '">Search for "' + escapeHtml(query) + '"</a>';

    this.listbox.innerHTML = html;
    this.options = Array.prototype.slice.call(this.listbox.querySelectorAll('[role="option"]'));
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');

    if (this.status) {
      this.status.textContent = products.length
        ? products.length + ' suggestions for "' + query + '". Use the up and down arrows to browse.'
        : 'No product suggestions for "' + query + '". Press Enter to search the whole store.';
    }

    this.open();
  };

  PredictiveSearch.prototype.open = function () {
    this.panel.hidden = false;
    this.isOpen = true;
    this.input.setAttribute('aria-expanded', 'true');
    this.container.classList.add('is-open');
  };

  PredictiveSearch.prototype.close = function () {
    window.clearTimeout(this.timer);
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
    this.panel.hidden = true;
    this.isOpen = false;
    this.activeIndex = -1;
    this.options = [];
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.container.classList.remove('is-open');
  };

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var containers = scope.querySelectorAll('[data-predictive-search]');
    Array.prototype.forEach.call(containers, function (container) {
      if (container.dataset.predictiveReady === 'true') return;
      container.dataset.predictiveReady = 'true';
      new PredictiveSearch(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();

